import { useEffect, useRef, useState } from 'react'
import {
  HTML_LANG,
  LOCALE_LABEL,
  LOCALE_PREFIX,
  SUPPORTED_LOCALES,
  pathInLocale,
  setUiLocale,
  splitLocalePath,
  useTranslation,
  type UiLocale,
} from '@lb/i18n'
import { cohortLabel, useMyCohort, useSetLocale } from '@lb/domain'
import { IconChevronRight, cn } from '@lb/ui'

import { auth, http, queryClient } from '../../lib/client'
import { shortName } from '../../lib/name'
import { SURFACE } from './nav'
import { useSessionContext } from '../providers/session'

/**
 * The signed-in member row + the popover it opens.
 *
 * ── Why this is one component ─────────────────────────────────────────────
 * The pill and its menu are two views of the same thing — pressing the pill
 * opens the menu, and the menu itself is what carries language, sign out,
 * and the legal links. Splitting them would make each half harder to place
 * and would leave the click handler two layers away from the menu that
 * actually consumes it. It also makes the outside-click and Escape wiring
 * one hook instead of three.
 *
 * ── What lives inside, and why ────────────────────────────────────────────
 * The design's mobile "More" sheet puts Sign out, language, and legal
 * behind the profile row rather than in the rail beside it — a rail
 * cluttered with account actions leaves the primary nav crowded, and none
 * of these are things a member does more than once a session. Two-tap sign
 * out is kept (a shared family device is exactly what the whole control
 * exists for). Privacy and Terms are single links; showing them here rather
 * than as an always-visible strip in the rail matches the mock.
 *
 * The theme toggle is deliberately NOT in here — the mock pins it to the
 * very bottom of the sidebar as a segmented pill, so it can be reached in
 * one click without opening a menu. `ThemePill` is the counterpart.
 */
export function ProfileMenu({ onNavigate }: { onNavigate: (to: string) => void }) {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  /*
   * Above the `!viewer` return, because hooks cannot be conditional. It reads
   * `useMyCohort`, and a fourth caller of that query in the shell
   * (`SurfacePanel` and `CohortMonthBadge` are the others) is a cache hit, not
   * a fourth request.
   */
  const planLine = usePlanLine()

  // Close on outside click and on Escape — a popover that can't be dismissed
  // is worse than one that doesn't open, so both are wired even though the
  // pill itself would work either way.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (!viewer) return null

  const { fullName, givenName, email, photoUrl } = viewer.session
  const initial = (fullName.trim() || email).charAt(0).toUpperCase()

  /*
   * `Marie L.`, not `Marie Lefebvre` — the mock's `profile-name`.
   *
   * The rail is 220px wide and the name shares it with an avatar, a plan line
   * and a chevron, so a full family name truncated mid-word. Shortening is
   * also the softer choice on a screen a child may be looking over: the app
   * says who is signed in without printing the family's surname on it.
   */
  const short = shortName(fullName, givenName, email)

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex min-h-[48px] w-full items-center gap-2.5 rounded-[var(--r-sm)] bg-cream-dark px-3 py-2 text-left transition-colors hover:bg-cream-darker"
      >
        {photoUrl ? (
          <img src={photoUrl} alt="" aria-hidden className="h-8 w-8 flex-none rounded-full object-cover" />
        ) : (
          <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-ink text-[13px] font-semibold text-cream">
            {initial}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-ink">{short}</span>
          <span className="block truncate text-[11.5px] text-muted">{planLine}</span>
        </span>
        <IconChevronRight
          size={16}
          className={cn('flex-none text-muted transition-transform', open && 'rotate-90')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute bottom-[calc(100%+8px)] left-0 right-0 z-30 rounded-[var(--r)] border border-border bg-surface p-2 shadow-lg"
        >
          <MenuItem
            onClick={() => {
              setOpen(false)
              onNavigate('/profile')
            }}
          >
            {t('profile.view')}
          </MenuItem>

          <LanguageRow />

          <div className="my-1 h-px bg-border-soft" aria-hidden />

          <MenuItem
            onClick={() => {
              setOpen(false)
              onNavigate('/legal/privacy')
            }}
          >
            {t('nav.privacy')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setOpen(false)
              onNavigate('/legal/terms')
            }}
          >
            {t('nav.terms')}
          </MenuItem>

          <div className="my-1 h-px bg-border-soft" aria-hidden />

          <SignOutRow />
        </div>
      ) : null}
    </div>
  )
}

function MenuItem({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex min-h-[36px] w-full items-center rounded-[var(--r-sm)] px-2 py-1.5 text-left text-[13px] text-ink-soft hover:bg-cream-dark hover:text-ink"
    >
      {children}
    </button>
  )
}

/**
 * Language row inside the profile menu.
 *
 * A segmented pair on one row, exactly like the standalone `LanguageToggle`
 * — kept here rather than rendering that component so its "small pill in
 * the middle of the rail" styling doesn't fight the menu's list layout.
 * Signs the visitor in? Then persist to `users.locale` too (the same
 * "language you are written to in" the standalone toggle writes).
 */
function LanguageRow() {
  const { t, i18n } = useTranslation()
  const { viewer } = useSessionContext()
  const setLocale = useSetLocale(http)
  const active: UiLocale = i18n.language === 'en' ? 'en' : 'fr'

  return (
    <div
      className="flex items-center gap-1 px-2 py-1.5"
      role="group"
      aria-label={t('language.label')}
    >
      <span className="flex-1 text-[13px] text-ink-soft">{t('language.label')}</span>
      {SUPPORTED_LOCALES.map((locale) => (
        <button
          key={locale}
          type="button"
          lang={HTML_LANG[locale]}
          aria-pressed={locale === active}
          onClick={() => {
            void setUiLocale(locale)
            if (viewer) setLocale.mutate(locale)
            const next = pathInLocale(window.location.pathname, locale)
            if (next !== window.location.pathname) {
              window.location.assign(next + window.location.search)
            }
          }}
          className={cn(
            'min-h-[32px] rounded-[var(--r-sm)] px-2 text-[12px]',
            locale === active ? 'bg-ink text-cream font-semibold' : 'text-muted hover:text-ink',
          )}
        >
          {LOCALE_LABEL[locale]}
        </button>
      ))}
    </div>
  )
}

/**
 * Sign out — two taps, then a full reload.
 *
 * Moved out of the always-visible rail and into the profile menu, per the
 * design mock's arrangement. The two-tap confirm is preserved: a
 * mis-clicked sign-out on a shared device costs whatever the parent was
 * mid-writing (a room reply, a bookmark), and one accidental tap should
 * not do that.
 *
 * The reload after clearing is the guarantee that no closure is still
 * holding the previous member's data — signing out is exactly the moment
 * to be certain rather than clever.
 */
function SignOutRow() {
  const { t } = useTranslation()
  const [armed, setArmed] = useState(false)

  if (!armed) {
    return (
      <button
        type="button"
        role="menuitem"
        onClick={() => setArmed(true)}
        className="flex min-h-[36px] w-full items-center rounded-[var(--r-sm)] px-2 py-1.5 text-left text-[13px] text-ink-soft hover:bg-cream-dark hover:text-ink"
      >
        {t('nav.signOut')}
      </button>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-2 px-2 py-1.5 text-[13px]">
      <span className="text-muted">{t('nav.signOutConfirm')}</span>
      <button
        type="button"
        onClick={() => {
          auth.clear()
          queryClient.clear()
          // Land on /login (locale-preserved), not the marketing root. A parent
          // signing out is far more likely to want back in than to be told to
          // create a fresh account.
          const { locale } = splitLocalePath(window.location.pathname)
          window.location.assign(`${LOCALE_PREFIX[locale]}/login`)
        }}
        className="font-semibold text-ink underline"
      >
        {t('nav.yes')}
      </button>
      <button type="button" onClick={() => setArmed(false)} className="text-muted underline">
        {t('nav.cancel')}
      </button>
    </div>
  )
}


/**
 * The pill's second line — `Pro · Org licence`, `TSP · Cohorte 2026`,
 * `Annual · Parent`.
 *
 * ── One rule, read from both ends ─────────────────────────────────────────
 * The three prototypes write it three ways, and they are not three rules:
 *
 * ```
 * app.jsx:24      plan: "Annual · Parent"
 * pro-app.jsx:21  plan: "Pro · Annual"
 * tsp-app.jsx:23  plan: "TSP · Cohorte 2026"
 * ```
 *
 * The **leading** slot says which product you are in; the **trailing** slot
 * says what kind of standing you hold in it. Parent has no product distinction
 * to draw — it is the default surface and carries no badge beside the wordmark
 * — so its leading slot falls back to naming the role instead. That is why the
 * parent form looks reversed and isn't.
 *
 * We had this as `planName · role` for everyone, which on the practitioner
 * surface printed **"Practitioner · Practitioner"**: the backend's plan is
 * named *Practitioner* and so is `role.PRO`. A line that says one word twice
 * has told the reader nothing, and it was the only line in the rail whose job
 * was to say which of four portals they were looking at.
 *
 * ── The trailing slot, per surface ────────────────────────────────────────
 * * **TSP** — the cohort. Every cohort member is on the same plan, bought as
 *   part of the programme, so the plan is not the useful half; *which* cohort
 *   is. Through `cohortLabel`, which falls back to the full name when no short
 *   form was authored.
 * * **PRO / ORG_ADMIN on an organisation seat** — `Org licence`. Not the plan
 *   name (`B2B · per seat`, which is a billing product and reads like one) but
 *   the fact that matters to the holder: somebody else renews this.
 * * **PRO / ORG_ADMIN paying for themselves** — the plan name.
 *
 * The design writes **`Pro · Annual`**, i.e. the billing *period*, and we
 * cannot: `GET /entitlement` carries `planName` but no period, and pro1's plan
 * is in fact monthly (`/subscriptions/mine` → `planVersion.period: "MONTH"`),
 * so defaulting the word to "Annual" would print a falsehood on the one account
 * we can check it against. Asked for in
 * `12-given-name-and-billing-period.md` §2; until it lands the plan name is the
 * truthful stand-in, and the day it lands this is the only line to change.
 */
function usePlanLine(): string {
  const { t } = useTranslation()
  const { viewer } = useSessionContext()
  const cohort = useMyCohort(http)

  if (!viewer) return ''

  const role = viewer.session.platformRole
  const access = viewer.access
  const surface = SURFACE[role]

  if (role === 'TSP' && cohort.data) {
    return `${SURFACE.TSP!.label} · ${cohortLabel(cohort.data.cohort)}`
  }

  /*
   * ── The design's word is the billing PERIOD ───────────────────────────────
   * `pro-app.jsx:21` writes `Pro · Annual`. We printed the plan name because
   * `/entitlement` carried no period; it does now (`MONTH | QUARTER | YEAR`,
   * request 12 §2), so this prints `Pro · Annual` as drawn — and it is null on
   * an organisation seat, which is the branch that names the organisation
   * instead and never reaches here.
   *
   * The plan name remains the fallback for a period we do not recognise.
   *
   * ── The English comparison came out ───────────────────────────────────────
   * This compared `planName` against the English role label to spot a French
   * collision, because the field answered `"Practitioner"` in every locale.
   * Round 14 fixed that at the source — it answers `Praticien` in French now —
   * so the comparison is a same-locale one again, and a client that knew which
   * language an API field was in has stopped needing to.
   */
  const roleLabel = t(`role.${role}`)
  const period = access?.period ? t(`billing.period.${access.period}`) : null
  const plan =
    period ??
    (access?.planName && access.planName.trim().toLowerCase() !== roleLabel.trim().toLowerCase()
      ? access.planName
      : roleLabel)

  const standing = access?.source === 'ORGANISATION' ? t('surface.orgLicence') : plan

  /*
   * No badge beside the wordmark means no product to name, so the role takes
   * the leading slot and the plan takes the trailing one — the parent form.
   * `filter(Boolean)` because a member with no entitlement at all has no
   * standing to print, and `Parent · ` with a dangling separator is worse than
   * `Parent`.
   */
  if (!surface) {
    // The parent form: plan leads, role follows. When the plan says nothing the
    // role has already been substituted in, so print it once.
    return standing === roleLabel ? roleLabel : [standing, roleLabel].filter(Boolean).join(' · ')
  }

  return [surface.label, standing].filter(Boolean).join(' · ')
}
